/* David Pladen — "Shop our Instagram" (sections/dp-instagram.liquid)
 * Track arrows, and the post window (.igx): previous/next, keyboard,
 * focus trap, add to cart through [data-dp-add] (assets/dp-cart-drawer.js),
 * share links and "copy link". Video posts play muted unless the visitor
 * prefers reduced motion.
 * Post data comes from Liquid in [data-ig-json].
 */

(() => {
  if (window.DpInstagramLoaded) return;
  window.DpInstagramLoaded = true;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const init = (root) => {
    if (root.dataset.bound) return;
    root.dataset.bound = 'true';
    const posts = JSON.parse(root.querySelector('[data-ig-json]').textContent || '[]');
    const track = root.querySelector('[data-ig-track]');
    const prev = root.querySelector('[data-ig-dir="-1"]');
    const next = root.querySelector('[data-ig-dir="1"]');
    const box = root.querySelector('[data-igx]');
    let index = 0;
    let trigger = null;

    const updateArrows = () => {
      prev.classList.toggle('off', track.scrollLeft < 4);
      next.classList.toggle('off', track.scrollLeft + track.clientWidth >= track.scrollWidth - 4);
    };
    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows, { passive: true });
    updateArrows();

    root.querySelectorAll('[data-ig-dir]').forEach((button) => {
      button.addEventListener('click', () => {
        const tile = track.querySelector('.ig-t');
        track.scrollBy({
          left: parseInt(button.dataset.igDir, 10) * (tile ? tile.offsetWidth : track.clientWidth),
          behavior: reduceMotion() ? 'auto' : 'smooth',
        });
      });
    });

    if (!reduceMotion()) {
      root.querySelectorAll('[data-ig-video]').forEach((video) => {
        video.muted = true;
        const play = video.play();
        if (play && play.catch) play.catch(() => {});
      });
    }

    const show = (i) => {
      index = (i + posts.length) % posts.length;
      const p = posts[index];
      let media;
      if (p.video) {
        media = document.createElement('video');
        media.src = p.video;
        if (p.img) media.poster = p.img;
        media.muted = true;
        media.loop = true;
        media.playsInline = true;
        media.controls = reduceMotion();
        media.setAttribute('aria-label', p.alt);
        if (!reduceMotion()) media.autoplay = true;
      } else {
        media = document.createElement('img');
        media.src = p.img;
        media.alt = p.alt;
      }
      const pane = box.querySelector('[data-igx-img]');
      box.querySelector('.igx-in').style.setProperty('--igx-r', p.ratio || 0.8);
      pane.replaceChildren(media);
      const status = box.querySelector('[data-share-status]');
      if (status) status.textContent = '';

      const th = box.querySelector('[data-igx-th]');
      th.innerHTML = '';
      if (p.thumb) {
        const t = document.createElement('img');
        t.src = p.thumb;
        t.alt = '';
        th.appendChild(t);
      }
      box.querySelectorAll('[data-igx-link]').forEach((a) => {
        if (p.url) a.href = p.url;
        else a.removeAttribute('href');
      });
      box.querySelector('[data-igx-price]').textContent = p.price;
      box.querySelector('[data-igx-name]').textContent = p.label ? `${p.name} · ${p.label}` : p.name;

      const add = box.querySelector('[data-igx-add]');
      add.hidden = !p.id;
      add.dataset.dpAdd = p.id;
      add.disabled = !p.available;
      add.textContent = p.available ? add.dataset.label : add.dataset.soldOut;

      box.querySelector('[data-igx-cap]').textContent = p.caption;
      const meta = box.querySelector('[data-igx-meta]');
      meta.textContent = p.meta;
      if (p.post) {
        const a = document.createElement('a');
        a.href = p.post;
        a.target = '_blank';
        a.rel = 'noopener';
        a.textContent = p.meta;
        meta.replaceChildren(a);
      }

      const pageUrl = p.url ? new URL(p.url, window.location.origin).href : window.location.href;
      const share = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`,
        email: `mailto:?subject=${encodeURIComponent(p.name)}&body=${encodeURIComponent(pageUrl)}`,
        x: `https://x.com/intent/post?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(p.caption)}`,
        pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(pageUrl)}&media=${encodeURIComponent(new URL(p.img, window.location.origin).href)}&description=${encodeURIComponent(p.caption)}`,
      };
      box.querySelectorAll('[data-share]').forEach((a) => {
        a.href = share[a.dataset.share];
      });
    };

    const open = (i, from) => {
      trigger = from;
      show(i);
      box.hidden = false;
      void box.offsetWidth;
      box.classList.add('on');
      document.documentElement.classList.add('dp-scroll-lock');
      // focus the window itself, so no button shows a focus ring on open
      const inner = box.querySelector('.igx-in');
      inner.tabIndex = -1;
      inner.focus({ preventScroll: true });
    };

    const close = () => {
      if (box.hidden) return;
      box.classList.remove('on');
      box.hidden = true;
      document.documentElement.classList.remove('dp-scroll-lock');
      trigger?.focus();
    };

    track.addEventListener('click', (event) => {
      const tile = event.target.closest('[data-ig]');
      if (!tile) return;
      event.preventDefault();
      open(parseInt(tile.dataset.ig, 10), tile);
    });

    box.addEventListener('click', (event) => {
      if (event.target === box || event.target.closest('[data-igx-close]')) {
        close();
        return;
      }
      const copy = event.target.closest('[data-share-copy]');
      if (copy) {
        const p = posts[index];
        const pageUrl = p.url ? new URL(p.url, window.location.origin).href : window.location.href;
        const status = box.querySelector('[data-share-status]');
        const done = () => {
          if (status) status.textContent = copy.dataset.copied || '';
        };
        if (navigator.clipboard?.writeText) navigator.clipboard.writeText(pageUrl).then(done, () => window.prompt('', pageUrl));
        else window.prompt('', pageUrl);
        return;
      }
      const step = event.target.closest('[data-igx-step]');
      if (step) show(index + parseInt(step.dataset.igxStep, 10));
      // Add to cart is handled by the site-wide [data-dp-add] listener; close so the drawer is visible
      if (event.target.closest('[data-igx-add]')) window.setTimeout(close, 50);
    });

    document.addEventListener('keydown', (event) => {
      if (box.hidden) return;
      if (event.key === 'Escape') close();
      else if (event.key === 'ArrowLeft') show(index - 1);
      else if (event.key === 'ArrowRight') show(index + 1);
      else if (event.key === 'Tab') {
        const items = Array.from(box.querySelectorAll('a[href], button:not([disabled]):not([hidden])'));
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });
  };

  const boot = (scope = document) => scope.querySelectorAll('[data-dp-ig]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
